import { useState } from 'react';
import { useAppStore } from '../store/useAppStore.js';
import { useToastStore } from '../store/useToastStore.js';
import { useAuth } from '../auth/index.js';
import { createTemplate, updateTemplate, deleteTemplate, deleteAccount, generateShareToken, revokeShareToken, updateProfile } from '../api/client.js';
import { GoalsSection } from '../components/settings/GoalsSection.jsx';
import { exportToCsv } from '../utils/exportCsv.js';

export function Settings() {
  const templates = useAppStore((s) => s.templates);
  const applications = useAppStore((s) => s.applications);
  const setTemplates = useAppStore((s) => s.setTemplates);
  const addToast = useToastStore((s) => s.addToast);
  const { signOut } = useAuth();

  const [editing, setEditing] = useState(null);
  const [itemInput, setItemInput] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const profile = useAppStore((s) => s.profile);
  const [shareToken, setShareToken] = useState(profile?.shareToken || null);
  const [displayName, setDisplayName] = useState(profile?.displayName || '');

  async function handleGenerateShare() {
    try {
      const { shareToken: token } = await generateShareToken();
      setShareToken(token);
      addToast('Share link created ✓', 'success');
    } catch { addToast('Failed to generate link', 'error'); }
  }

  async function handleRevokeShare() {
    try {
      await revokeShareToken();
      setShareToken(null);
      addToast('Share link revoked ✓', 'success');
    } catch { addToast('Failed to revoke link', 'error'); }
  }

  function copyShareLink() {
    const url = `${window.location.origin}/shared/${shareToken}`;
    navigator.clipboard.writeText(url);
    addToast('Link copied ✓', 'success');
  }

  async function handleDeleteAccount() {
    setDeleting(true);
    try {
      await deleteAccount();
      localStorage.removeItem("onboarded");
      await signOut();
    } catch {
      addToast('Failed to delete account', 'error');
      setDeleting(false);
    }
  }

  function startNew() {
    setEditing({ templateName: '', items: [] });
    setItemInput('');
  }

  function startEdit(t) {
    setEditing({ templateId: t.templateId, templateName: t.templateName, items: [...t.items] });
    setItemInput('');
  }

  function addItem() {
    const text = itemInput.trim();
    if (!text) return;
    setEditing((e) => ({ ...e, items: [...e.items, { id: crypto.randomUUID(), text, completed: false }] }));
    setItemInput('');
  }

  function removeItem(idx) {
    setEditing((e) => ({ ...e, items: e.items.filter((_, i) => i !== idx) }));
  }

  async function handleSave() {
    if (!editing.templateName.trim()) return;
    try {
      if (editing.templateId) {
        const updated = await updateTemplate(editing.templateId, {
          templateName: editing.templateName.trim(),
          items: editing.items,
        });
        setTemplates(templates.map((t) => (t.templateId === editing.templateId ? updated : t)));
      } else {
        const created = await createTemplate({
          templateName: editing.templateName.trim(),
          items: editing.items,
        });
        setTemplates([...templates, created]);
      }
      setEditing(null);
      addToast('Template saved ✓', 'success');
    } catch {
      addToast('Failed to save template', 'error');
    }
  }

  async function handleDelete(templateId) {
    try {
      await deleteTemplate(templateId);
      setTemplates(templates.filter((t) => t.templateId !== templateId));
      addToast('Template deleted ✓', 'success');
    } catch {
      addToast('Failed to delete template', 'error');
    }
  }

  return (
    <div className="p-6 space-y-6 max-w-2xl">
      <h1 className="text-lg font-semibold text-slate-900">Settings</h1>

      {/* Profile */}
      <section>
        <h2 className="text-sm font-medium text-slate-700 mb-2">Profile</h2>
        <div className="flex gap-2 items-end">
          <div className="flex-1">
            <label className="block text-xs text-slate-500 mb-1">Display Name (shown on shared link)</label>
            <input
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="Jonny"
              className="w-full rounded border border-slate-300 px-3 py-1.5 text-base focus:outline-none focus:ring-1 focus:ring-primary-500"
            />
          </div>
          <button
            onClick={async () => { await updateProfile({ displayName: displayName.trim() }); addToast('Name saved ✓', 'success'); }}
            className="rounded bg-primary-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-primary-700"
          >
            Save
          </button>
        </div>
      </section>

      <GoalsSection />

      <section>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-medium text-slate-700">Checklist Templates</h2>
          {!editing && (
            <button
              onClick={startNew}
              className="rounded bg-primary-600 px-3 py-1 text-xs font-medium text-white hover:bg-primary-700"
            >
              + New Template
            </button>
          )}
        </div>

        {templates.length === 0 && !editing && (
          <p className="text-sm text-slate-400 italic">No templates yet. Create one to reuse across applications.</p>
        )}

        {/* Template list */}
        {!editing && templates.map((t) => (
          <div key={t.templateId} className="rounded border border-slate-200 p-3 mb-2">
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium text-slate-900">{t.templateName}</p>
              <div className="flex gap-2">
                <button onClick={() => startEdit(t)} className="text-xs text-primary-600 hover:underline">Edit</button>
                <button onClick={() => handleDelete(t.templateId)} className="text-xs text-red-500 hover:underline">Delete</button>
              </div>
            </div>
            <ul className="mt-1 space-y-0.5">
              {t.items.map((item, i) => (
                <li key={item.id || i} className="text-xs text-slate-500">• {item.text}</li>
              ))}
            </ul>
          </div>
        ))}

        {/* Edit/Create form */}
        {editing && (
          <div className="rounded border border-slate-200 p-4 space-y-3">
            <input
              type="text"
              placeholder="Template name"
              value={editing.templateName}
              onChange={(e) => setEditing((s) => ({ ...s, templateName: e.target.value }))}
              className="w-full rounded border border-slate-300 px-3 py-1.5 text-base focus:outline-none focus:ring-1 focus:ring-primary-500"
            />
            <ul className="space-y-1">
              {editing.items.map((item, i) => (
                <li key={item.id || i} className="flex items-center gap-2 text-sm text-slate-700">
                  <span className="flex-1">• {item.text}</span>
                  <button onClick={() => removeItem(i)} className="text-xs text-red-400 hover:text-red-600">×</button>
                </li>
              ))}
            </ul>
            <div className="flex gap-2">
              <input
                type="text"
                placeholder="Add item"
                value={itemInput}
                onChange={(e) => setItemInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addItem())}
                className="flex-1 rounded border border-slate-300 px-3 py-1.5 text-base focus:outline-none focus:ring-1 focus:ring-primary-500"
              />
              <button onClick={addItem} className="rounded border border-slate-300 px-3 py-1.5 text-xs text-slate-600 hover:bg-slate-50">Add</button>
            </div>
            <div className="flex gap-2 pt-2">
              <button onClick={handleSave} className="rounded bg-primary-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-primary-700">
                Save
              </button>
              <button onClick={() => setEditing(null)} className="rounded px-3 py-1.5 text-xs text-slate-600 hover:bg-slate-100">
                Cancel
              </button>
            </div>
          </div>
        )}
      </section>

      {/* Export */}
      <section className="border-t border-slate-200 pt-6">
        <h2 className="text-sm font-medium text-slate-700 mb-2">Export Data</h2>
        <p className="text-xs text-slate-500 mb-3">Download all your applications as a CSV file.</p>
        <button
          onClick={() => exportToCsv(applications)}
          className="rounded border border-slate-300 px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-50"
        >
          Export CSV
        </button>
      </section>

      {/* Share Link */}
      <section className="border-t border-slate-200 pt-6">
        <h2 className="text-sm font-medium text-slate-700 mb-2">Share Link</h2>
        <p className="text-xs text-slate-500 mb-3">Generate a read-only link to share your job search progress with an accountability partner. No sensitive details (company names, salaries, notes) are exposed.</p>
        {shareToken ? (
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <code className="flex-1 rounded bg-slate-100 px-3 py-1.5 text-xs text-slate-700 truncate">{`${window.location.origin}/shared/${shareToken}`}</code>
              <button onClick={copyShareLink} className="rounded border border-slate-300 px-3 py-1.5 text-xs text-slate-600 hover:bg-slate-50">Copy</button>
            </div>
            <button onClick={handleRevokeShare} className="rounded px-3 py-1.5 text-xs text-red-600 hover:bg-red-50">Revoke Link</button>
          </div>
        ) : (
          <button onClick={handleGenerateShare} className="rounded border border-slate-300 px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-50">Generate Share Link</button>
        )}
      </section>

      {/* Show Onboarding */}
      <section>
        <h2 className="text-sm font-medium text-slate-700 mb-2">Onboarding</h2>
        <button
          onClick={() => { localStorage.removeItem('onboarded'); addToast('Onboarding will show on next Dashboard visit ✓', 'success'); }}
          className="rounded border border-slate-300 px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-50"
        >
          Show Welcome Guide Again
        </button>
      </section>

      {/* Delete Account */}
      <section className="border-t border-slate-200 pt-6">
        <h2 className="text-sm font-medium text-red-600 mb-2">Danger Zone</h2>
        {!confirmDelete ? (
          <button
            onClick={() => setConfirmDelete(true)}
            className="rounded border border-red-300 px-3 py-1.5 text-sm text-red-600 hover:bg-red-50"
          >
            Delete Account
          </button>
        ) : (
          <div className="rounded border border-red-300 bg-red-50 p-4 space-y-3">
            <p className="text-sm text-red-800">
              This will permanently delete your account and all application data. This action is irreversible.
            </p>
            <div className="flex gap-2">
              <button
                onClick={handleDeleteAccount}
                disabled={deleting}
                className="rounded bg-red-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
              >
                {deleting ? 'Deleting…' : 'Yes, delete my account'}
              </button>
              <button
                onClick={() => setConfirmDelete(false)}
                className="rounded px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-100"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
